import React, { Children } from 'react';

/**
 * Returns `true` if `child` is a `React.Fragment`.
 * @param {ReactNode | ExoticComponent} child
 * @returns boolean
 */
function isFragment(child) {
  return !!child && child.type === React.Fragment;
}
/**
 * Returns element's displayName, name or type depending on what is given.
 * @param {ReactNode} element
 * @returns {string} Element's displayName, name or type
 */
function getElementName(element) {
  if (element) {
    const typeCastE = element;
    if (typeCastE.displayName) {
      return typeCastE.displayName;
    }
    if (typeCastE.type) {
      return typeof typeCastE.type === 'string' ? typeCastE.type : typeCastE.type.displayName || typeCastE.type.name;
    }
  }
  return typeof element;
}
/**
 * Returns `true` if `element` is a valid component whose `displayName` matches
 * the given `displayName`.
 * @param {ReactNode} element
 * @param {string} displayName
 * @returns boolean
 */
function isOfType(element, displayName) {
  const elementName = getElementName(element);
  return elementName.includes(displayName);
}
/**
 * Applies a function `fn` to an array of children. If a `React.Fragment` is found,
 * recursively applies the same function to its `children`.
 * @param {array} children
 * @param {function} fn
 * @returns array
 */
function mapRecursively(children, fn) {
  return Children.map(children, (child, i) => {
    if (child && isFragment(child)) {
      return Children.map(
      // @ts-expect-error assume there is a children prop
      child.props.children, grandChild => /*#__PURE__*/React.isValidElement(grandChild) && fn(grandChild, i));
    }
    return /*#__PURE__*/React.isValidElement(child) ? fn(child, i) : child;
  });
}
/**
 * Strips down Injecss() HOC name from components
 * @param {string} compName
 * @returns string
 */
function getUninjectedCompName(compName) {
  return compName.replace('Injecss(', '').replace(')', '');
}
/**
 * Filters children based on displayName. Also iterates through Fragments recursively.
 * @param {array} children
 * @param {string} displayName
 * @returns A collection of children that has the provided displayName
 */
function getChildrenByName(children, displayName) {
  let filteredChildren = [];
  Children.forEach(children, child => {
    if (isFragment(child)) {
      const nestedChildren = getChildrenByName(
      // @ts-expect-error assume there is a children prop
      child.props.children, displayName);
      filteredChildren = [...filteredChildren, ...nestedChildren];
    } else if (child && getUninjectedCompName(getElementName(child)) === displayName) {
      filteredChildren.push(child);
    }
  });
  return filteredChildren;
}
/**
 * Assigns ref to both innerRef and component internal ref variable.
 * Does nothing if null passed as node (preventing node callback double passes, see: https://react.dev/reference/react-dom/components/common#ref-callback)
 * @param node HTMl element
 * @param innerRef inner ref object
 * @param componentRef component ref object
 */
function assignInnerRefWithComponentRef(node, innerRef, componentRef) {
  if (node === null) {
    return;
  }
  componentRef.current = node;
  if (typeof innerRef === 'function') {
    innerRef(node);
  } else if (innerRef) {
    // TODO resolve type assignment with `as`
    innerRef.current = node;
  }
}

export { assignInnerRefWithComponentRef, getChildrenByName, getElementName, getUninjectedCompName, isFragment, isOfType, mapRecursively };
